import { Box, Chip, Divider, Stack, Typography, useTheme, alpha } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { RoadmapPhase, RoadmapPhaseStatus } from "../../api/client";
import WeekRail from "./WeekRail";

interface PhaseSectionProps {
  phase: RoadmapPhase;
  compact?: boolean;
  focusTaskId?: string;
  onToggle: (taskId: string, next: boolean) => void;
}

const NUM_TILE_SIZE = 48;

// Labels are i18n keys, not literal text — resolved via t() where rendered
// (PhaseSection.status* in roadmap.json). See AppRail.tsx's PRODUCT_META
// labelKey precedent.
const STATUS_LABEL_KEY: Record<RoadmapPhaseStatus, string> = {
  done: "PhaseSection.statusDone",
  current: "PhaseSection.statusCurrent",
  upcoming: "PhaseSection.statusUpcoming",
};

// Spec §6.3 — head grid: 48px num · 1fr title · auto chip. Number tile and
// status chip both use the status color system. Summary ≤ 2 sentences with
// a strong end-state claim — we render whatever the LLM emits; markdown-to-
// React rich-text rendering is deferred to v1.1.
//
// `compact` mode (used by orchestrator for phases 3+4) renders the phase
// head + week titles only — task cards are hidden via WeekRail's compact
// prop. Spec §5.2 — protects week-3 user from week-12 anxiety.
export default function PhaseSection({
  phase,
  compact = false,
  focusTaskId,
  onToggle,
}: PhaseSectionProps) {
  const theme = useTheme();
  const { t } = useTranslation("roadmap");
  const weeks = phase.weeks ?? [];
  const totalTasks = weeks.reduce((acc, w) => acc + (w.tasks?.length ?? 0), 0);
  const doneTasks = weeks.reduce(
    (acc, w) => acc + (w.tasks?.filter((task) => task.done).length ?? 0),
    0,
  );

  const tileColors = phaseNumberTileColors(theme, phase.status);
  const chipColors = statusChipColors(theme, phase.status);

  return (
    <Box
      component="section"
      id={`phase-${phase.index}`}
      data-phase-index={phase.index}
      sx={{ scrollMarginTop: "88px" }}
    >
      {/* Head: num tile · title block · status chip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: `${NUM_TILE_SIZE}px 1fr`, sm: `${NUM_TILE_SIZE}px 1fr auto` },
          alignItems: "center",
          columnGap: 2,
          rowGap: 1,
          mb: 2,
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: NUM_TILE_SIZE,
            height: NUM_TILE_SIZE,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: tileColors.bg,
            color: tileColors.fg,
            fontWeight: 500,
            fontSize: 22,
            fontFamily: "monospace",
            letterSpacing: "-0.02em",
          }}
        >
          {phase.index}
        </Box>
        <Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{ fontWeight: 500, lineHeight: 1.2 }}
          >
            {phase.title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", letterSpacing: "0.04em", mt: 0.25, display: "block" }}
          >
            {t("PhaseSection.weekRange", { low: phase.week_range[0], high: phase.week_range[1] })}
            {totalTasks > 0 && (
              <>
                {" · "}
                {t("PhaseSection.tasksProgress", { done: doneTasks, total: totalTasks })}
              </>
            )}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={t(STATUS_LABEL_KEY[phase.status])}
          sx={{
            gridColumn: { xs: "2 / 3", sm: "auto" },
            justifySelf: { xs: "start", sm: "end" },
            bgcolor: chipColors.bg,
            color: chipColors.fg,
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            border: "1px solid",
            borderColor: chipColors.border,
          }}
        />
      </Box>

      {/* Summary */}
      {phase.summary && (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            lineHeight: 1.6,
            mb: 3,
            pl: { xs: 0, sm: `${NUM_TILE_SIZE + 16}px` },
          }}
        >
          {phase.summary}
        </Typography>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Week rail */}
      <Stack spacing={3}>
        {weeks.map((week, idx) => (
          <WeekRail
            key={week.index}
            week={week}
            isFirstInPhase={idx === 0}
            compact={compact}
            focusTaskId={focusTaskId}
            onToggle={onToggle}
          />
        ))}
      </Stack>
    </Box>
  );
}

function phaseNumberTileColors(
  theme: Theme,
  status: RoadmapPhaseStatus,
) {
  if (status === "done") {
    return {
      bg: alpha(theme.palette.success.main, 0.15),
      fg: theme.palette.success.dark,
    };
  }
  if (status === "current") {
    return {
      bg: alpha(theme.palette.secondary.main, 0.15),
      fg: theme.palette.secondary.dark,
    };
  }
  return {
    bg: alpha(theme.palette.text.primary, 0.06),
    fg: theme.palette.text.secondary,
  };
}

function statusChipColors(
  theme: Theme,
  status: RoadmapPhaseStatus,
) {
  if (status === "done") {
    return {
      bg: alpha(theme.palette.success.main, 0.12),
      fg: theme.palette.success.dark,
      border: alpha(theme.palette.success.main, 0.4),
    };
  }
  if (status === "current") {
    return {
      bg: alpha(theme.palette.secondary.main, 0.12),
      fg: theme.palette.secondary.dark,
      border: alpha(theme.palette.secondary.main, 0.5),
    };
  }
  return {
    bg: "transparent",
    fg: theme.palette.text.secondary,
    border: alpha(theme.palette.text.primary, 0.2),
  };
}
